import { computed, Ref, ref } from '@vue/composition-api';
import { i18n } from '@tt4b/ttam-i18n';
import {
  AdgroupFormData,
  AdStatusValues,
  FullNormalFormData,
  SupportDvObjectives,
  SupportVastMoatObjectives,
  SupportVastUrlObjectives,
  ViewabilityTrackTypeMap,
  CampaignFormData,
} from '@tt4b/ttam-enums';
import { useAllowlist, useCreativeActionTypeIs, useStdCreativeAdgroupIs } from '@tt4b/ttam-hooks';

export function useViewAbility(options: {
  adgroupData: Ref<AdgroupFormData>;
  creativeData: Ref<FullNormalFormData>;
  campaignData: Ref<CampaignFormData>;
  createMode: Ref<string>;
}) {
  const { adgroupData, creativeData, campaignData, createMode } = options;

  const { isMoatWhite, isDvWhite, isViewIASWhite } = useAllowlist();
  const { isEdit } = useCreativeActionTypeIs({
    createMode,
    adgroupFormData: adgroupData,
  });
  const objectiveType = computed(() => adgroupData.value.objectiveType);
  const auditStatus = computed(() => (adgroupData.value as any).auditStatus);

  const { isSupportViewabilityTracking } = useStdCreativeAdgroupIs({
    adgroupFormData: adgroupData,
    campaignFormData: campaignData,
  });

  /** 是否展示项目 */
  const isShowDv = computed(() => SupportDvObjectives.includes(objectiveType.value as number) && isDvWhite.value);
  const isShowMoat = computed(
    () => SupportVastMoatObjectives.includes(objectiveType.value as number) && isMoatWhite.value,
  );
  const isShowViewIAS = computed(
    () => SupportVastUrlObjectives.includes(objectiveType.value as number) && isViewIASWhite.value,
  );
  const isShowViewability = computed(
    () => isSupportViewabilityTracking.value && (isShowMoat.value || isShowDv.value || isShowViewIAS.value),
  );

  /** 禁用项目 */
  const isDisabledDv = computed(() => isEdit.value && auditStatus.value !== AdStatusValues.AUDIT);
  const isDisabledMoat = computed(() => isEdit.value && auditStatus.value !== AdStatusValues.AUDIT);
  const isDisabledViewIAS = computed(() => isEdit.value && auditStatus.value !== AdStatusValues.AUDIT);
  const isDisabledViewability = computed(() => isDisabledMoat.value || isDisabledDv.value || isDisabledViewIAS.value);

  /** 所有可见行选项 */
  const allViewabilityOptions = computed(() => [
    {
      value: ViewabilityTrackTypeMap.MOAT,
      valKey: 'vastMoat',
      label: i18n.t('module_track_url_moat_checkbox_label'),
      hide: !isShowMoat.value,
      disabled: isDisabledMoat.value,
    },
    {
      value: ViewabilityTrackTypeMap.DV,
      valKey: 'vastDoubleVerify',
      label: i18n.t('module_ad_dv_switch_title'),
      hide: !isShowDv.value,
      disabled: isDisabledDv.value,
    },
    {
      value: ViewabilityTrackTypeMap.IAS,
      valKey: 'vastUrl',
      label: i18n.t('module_track_url_ias_checkbox_label'),
      hide: !isShowViewIAS.value,
      disabled: isDisabledViewIAS.value,
    },
  ]);

  /** 可使用的可见性选项 */
  const availableViewabilityOptions = computed(() => allViewabilityOptions.value.filter(i => !i.hide));

  const isOnlyOneViewTrackType = computed(() => availableViewabilityOptions.value.length === 1);
  const isOnlyShowMoat = computed(() => isShowMoat.value && isOnlyOneViewTrackType.value);
  const isOnlyShowDV = computed(() => isShowDv.value && isOnlyOneViewTrackType.value);
  const isOnlyShowViewIAS = computed(() => isShowViewIAS.value && isOnlyOneViewTrackType.value);

  /** 可见性监测标题 */
  const viewabilityTrackTitle = computed(() => {
    if (isOnlyShowMoat.value) {
      return i18n.t('module_track_url_view_title_with_moat');
    }
    if (isOnlyShowDV.value) {
      return i18n.t('module_track_url_view_title_with_dv');
    }
    if (isOnlyShowViewIAS.value) {
      return i18n.t('module_track_url_view_title_with_ias');
    }

    return i18n.t('module_track_url_view_title');
  });

  // 设置可见性Track类别, 传参0或不传表示初始化
  function onViewTrackDataChange(optionType = 0) {
    let vastMoat = 0;
    let vastDoubleVerify = 0;
    let vastIas = false;
    let vastUrl;

    switch (optionType) {
      case ViewabilityTrackTypeMap.MOAT:
        vastMoat = 1;
        break;
      case ViewabilityTrackTypeMap.DV:
        vastDoubleVerify = 1;
        break;
      case ViewabilityTrackTypeMap.IAS:
        vastIas = true;
        vastUrl = creativeData.value.vastUrl;
        break;
      default:
    }

    return { vastMoat, vastDoubleVerify, vastIas, vastUrl };
  }

  return {
    isShowViewability,
    isDisabledViewability,
    isOnlyShowDV,
    isOnlyShowMoat,
    isOnlyShowViewIAS,

    viewabilityTrackTitle,
    allViewabilityOptions,
    availableViewabilityOptions,
    isOnlyOneViewTrackType,

    onViewTrackDataChange,
  };
}
